import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const ProtectedRoute = ({ children, requiredRole = null, allowedRoles = null, fallbackPath = '/login-register' }) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Prevent redirect loops
  const lastRedirectTime = useRef(0);
  const redirectCount = useRef(0);
  const lastPathRef = useRef('');
  
  // Track if component is mounted
  const isMounted = useRef(true);
  
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Store last known role in sessionStorage to track changes
  useEffect(() => {
    if (userProfile?.role) {
      sessionStorage.setItem('lastKnownRole', userProfile.role);
    }
  }, [userProfile]);

  // If still loading, show loading indicator but with a delay to prevent flashing
  // Also, store loading state in sessionStorage to prevent redirect loops during loading
  const [showLoading, setShowLoading] = useState(false);
  
  useEffect(() => {
    // Only show loading after a short delay to prevent flashing
    let loadingTimer;
    
    if (loading) {
      // Check if we've been loading for too long (possible loop)
      const loadingStartTime = sessionStorage.getItem('authLoadingStartTime');
      const now = Date.now();
      
      if (!loadingStartTime) {
        // First time loading, record start time
        sessionStorage.setItem('authLoadingStartTime', now.toString());
      } else if (now - parseInt(loadingStartTime) > 10000) {
        // If loading for more than 10 seconds, something might be wrong
        // Clear the loading start time to reset the counter
        sessionStorage.removeItem('authLoadingStartTime');
        // Force proceed with whatever auth state we have
        return;
      }
      
      // Set a delay before showing loading indicator
      loadingTimer = setTimeout(() => {
        if (isMounted.current) {
          setShowLoading(true);
        }
      }, 500);
    } else {
      // Not loading anymore, clear the loading start time
      sessionStorage.removeItem('authLoadingStartTime');
      setShowLoading(false);
    }
    
    return () => {
      clearTimeout(loadingTimer);
    };
  }, [loading]);
  
  if (loading) {
    // If we've just started loading, don't show anything yet to prevent flashing
    if (!showLoading) {
      return null;
    }
    
    // After delay, show loading indicator
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    // Prevent redirect loops by checking time since last redirect
    const now = Date.now();
    const timeSinceLastRedirect = now - lastRedirectTime.current;
    
    // If we're redirecting too frequently, it might be a loop
    if (timeSinceLastRedirect < 2000) {
      redirectCount.current += 1;
      
      // If we've redirected too many times in a short period, break the loop
      if (redirectCount.current > 3) {
        // Emergency fallback to prevent infinite loops
        return (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center p-6 max-w-md">
              <div className="text-error-500 text-5xl mb-4">⚠️</div>
              <h1 className="text-xl font-bold mb-2">Authentication Error</h1>
              <p className="mb-4">We detected an authentication loop. This might be due to a session issue.</p>
              <div className="flex justify-center space-x-4">
                <a href="/" className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">Home</a>
                <a href="/login-register" className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary-dark">Login</a>
              </div>
            </div>
          </div>
        );
      }
    } else {
      // Reset counter if enough time has passed
      redirectCount.current = 1;
    }
    
    lastRedirectTime.current = now;
    
    // Store the location they tried to visit for redirect after login
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Wait for user profile to load
  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Check role-based access if required
  const hasAccess = () => {
    if (!userProfile?.role) return false;
    
    // If allowedRoles is provided, check if user role is in the allowed list
    if (allowedRoles && Array.isArray(allowedRoles)) {
      return allowedRoles.includes(userProfile.role);
    }
    
    // If requiredRole is provided, check exact match
    if (requiredRole) {
      return userProfile.role === requiredRole;
    }
    
    // If no role restrictions, allow access
    return true;
  };

  const getRoleDisplayText = () => {
    if (allowedRoles && Array.isArray(allowedRoles)) {
      return allowedRoles.join('s, ') + 's';
    }
    if (requiredRole) {
      return requiredRole + 's';
    }
    return 'authorized users';
  };

  if (!hasAccess()) {
    // Show unauthorized access message instead of automatic redirect
    // to prevent confusion and redirect loops
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-6 max-w-md">
          <div className="text-warning-500 text-5xl mb-4">🚫</div>
          <h1 className="text-xl font-bold mb-2">Access Restricted</h1>
          <p className="mb-4">
            This page is only accessible to {getRoleDisplayText()}.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary-dark"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;